import { Check, FileUp, Info, RotateCw, TriangleAlert, X } from "lucide-react"
import * as React from "react"
import { useSearchParams } from "react-router-dom"

import { TRANSACTIONS_TABS } from "@/components/layout/nav"
import { PageHeader } from "@/components/layout/page-header"
import { TabStrip } from "@/components/layout/tab-strip"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Notice } from "@/components/ui/notice"
import { Skeleton } from "@/components/ui/skeleton"
import {
  getCoverage,
  listUploads,
  startUpload,
  UploadAborted,
  type HistoryCoverage,
  type Stretch,
  type Upload,
  type UploadHandle,
  type UploadReport,
  type UploadState,
} from "@/lib/api"
import { EMPTY, formatBytes, formatDateFull, formatInt, formatTime } from "@/lib/format"
import { cn } from "@/lib/utils"

/**
 * Экран заливки выгрузки (AMEND-16 §3 шаг 3).
 *
 * Владелица выбирает файл сама — без SSH и без загрузчика на сервере. Дальше
 * работают ручки шага 3A: приём отвечает номером задачи до разбора, разбор идёт
 * вне запроса, состояние лежит в базе.
 *
 * **Ход берётся с сервера по номеру задачи, а не из памяти вкладки.** Это и
 * есть главное свойство экрана, а не деталь исполнения: страница ничего не
 * копит у себя, поэтому закрытая вкладка ничего не теряет, а открытая заново —
 * ничего не начинает сначала. Номер разобранной задачи живёт в адресе
 * (`?job=12`), так что и ссылку можно отдать, и перезагрузка возвращает ровно
 * ту заливку, на которую смотрели.
 *
 * **Ни одной ячейки выгрузки здесь нет** (AMEND-16 §5). На экран выходят
 * счётчики, имена колонок, даты покрытия, имя выбранного файла и тип исключения
 * — ручки 3A другого и не отдают. Ни `email`, ни `ip`, ни `card_hash` сюда
 * физически неоткуда взять, и это проверяется на стороне ручки, а не обещанием
 * экрана.
 *
 * Оформление — той же консоли: полотно, белые панели, надписи конденсированным
 * шрифтом в разрядку. Своей эстетики экран не заводит: человек попадает сюда с
 * вкладки транзакций, и смена языка форм читалась бы как переход в чужую
 * программу.
 *
 * Лежит **внутри** `pages/transactions/`, а не в соседнем `pages/uploads/`, и
 * это не вкусовщина: `scripts/safe-rsync.sh` среди дефолтных исключений несёт
 * `--exclude=uploads/`, шаблон без ведущей косой матчит каталог на любой
 * глубине, а исключения врапперки стоят **до** пользовательских — перебить их
 * своим `--include` из команды выката нельзя. Проверено сухим прогоном
 * 2026-09-19: с исключением каталог не попадал в список вовсе. Отказ был бы
 * молчаливым — сборка на сервере зелёная, маршрут `/transactions/import` отдаёт
 * 404, и искать пришлось бы в роутере. По смыслу файл всё равно на месте:
 * заливка — вкладка раздела Transactions, рядом со списком и карточкой.
 */

/** Как часто спрашивать состояние, пока что-то идёт. */
const LIVE_MS = 1500

/**
 * И как часто — когда всё закончено.
 *
 * Совсем останавливать опрос нельзя: задачу могла завести соседняя вкладка или
 * подобрать `sweep` после рестарта бэкенда, и замерший экран показывал бы
 * «заливок нет» при идущей заливке.
 */
const IDLE_MS = 15000

function isActive(job: Upload) {
  return job.status === "queued" || job.status === "running"
}

/**
 * Цвет статуса.
 *
 * `failed` — единственный красный: в палитре консоли красный занят под исход
 * скоринга, и раздавать его предупреждениям значило бы стереть разницу между
 * «отказало» и «обратите внимание».
 */
const STATUS_TONE: Record<UploadState, "neutral" | "accent" | "approve" | "review" | "decline"> = {
  queued: "neutral",
  running: "accent",
  done: "approve",
  failed: "decline",
  cancelled: "review",
}

/**
 * Сутки покрытия — в том же поясе, в котором их считает реестр.
 *
 * Не `formatDateFull`: тот разбирает `2026-06-16` как полночь UTC и печатает её
 * **по поясу браузера**, а западнее Гринвича это уже пятнадцатое. Здесь дата —
 * не момент, а метка суток, и сдвинутая на сутки граница дыры означала бы, что
 * индикатор называет не те даты, ради точности которых он и заведён.
 */
const DAY = new Intl.DateTimeFormat("en-US", {
  month: "numeric",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
})

function Day({ value }: { value: string | null }) {
  const parsed = value ? new Date(`${value}T00:00:00Z`) : null
  return (
    <span>
      {parsed && !Number.isNaN(parsed.getTime()) ? DAY.format(parsed) : EMPTY}
    </span>
  )
}

export function ImportPage() {
  const [jobs, setJobs] = React.useState<Upload[] | null>(null)
  const [listError, setListError] = React.useState<string | null>(null)
  const [file, setFile] = React.useState<File | null>(null)
  /** Сколько байт уже ушло на сервер. `null` — сейчас ничего не отправляем. */
  const [sent, setSent] = React.useState<number | null>(null)
  const [sendError, setSendError] = React.useState<string | null>(null)
  const [params, setParams] = useSearchParams()
  /**
   * Повод перечитать индикатор глубины.
   *
   * Отдельным счётчиком, а не опросом в такт списку: реестр покрытия двигает
   * только **завершившаяся** заливка, и спрашивать глубину каждые полторы
   * секунды означало бы гонять `range_agg` ради заведомо того же ответа.
   * Растёт, когда в списке появился новый `done`, и по кнопке Refresh — ею же
   * снимается и устаревший за ночь ответ: глубина меряется назад от сегодня.
   */
  const [beat, setBeat] = React.useState(0)
  const settled = React.useRef<string | null>(null)

  const transfer = React.useRef<UploadHandle | null>(null)
  const fileInput = React.useRef<HTMLInputElement>(null)
  /** Разбудить опрос вне очереди — после отправки и по кнопке. */
  const wake = React.useRef<() => void>(() => {})

  /*
   * Опрос — самозаводящийся `setTimeout`, а не `setInterval` и не эффект,
   * зависящий от своего же состояния.
   *
   * Зависимости пустые намеренно. Поставь сюда `jobs` — каждый ответ пересобирал
   * бы эффект, а уборка обрывала бы следующий запрос, который сама же и завела;
   * экран при этом выглядел бы застрявшим в загрузке при нулевых ошибках в
   * консоли. Частота решается по **только что полученному** ответу, а не по
   * состоянию, поэтому знать о нём эффекту незачем.
   */
  React.useEffect(() => {
    let stopped = false
    let timer = 0

    async function tick() {
      try {
        const list = await listUploads()
        if (stopped) return
        setJobs(list)
        setListError(null)
        // Ключ по завершённым заливкам: пока он тот же, реестру покрытия
        // взяться нечему. Первый ответ ключ только запоминает — индикатор уже
        // спросил своё на монтировании.
        const done = list.filter((job) => job.status === "done").map((job) => job.id).join(",")
        if (settled.current !== null && settled.current !== done) setBeat((was) => was + 1)
        settled.current = done
        timer = window.setTimeout(tick, list.some(isActive) ? LIVE_MS : IDLE_MS)
      } catch (cause) {
        if (stopped) return
        // Прошлый ответ на экране оставляем: упавший опрос — это «свежее не
        // принесли», а не «заливок больше нет».
        setListError(cause instanceof Error ? cause.message : "Request failed")
        timer = window.setTimeout(tick, IDLE_MS)
      }
    }

    wake.current = () => {
      window.clearTimeout(timer)
      void tick()
    }
    void tick()

    return () => {
      stopped = true
      window.clearTimeout(timer)
    }
  }, [])

  async function send() {
    if (!file || sent !== null) return
    setSendError(null)
    setSent(0)

    const handle = startUpload(file, (loaded) => setSent(loaded))
    transfer.current = handle
    try {
      const job = await handle.done
      // Задача уже заведена — показываем её немедленно, не дожидаясь опроса:
      // пауза между «отправлено» и первым ответом списка читалась бы как
      // «файл ушёл в никуда».
      setJobs((prev) => [job, ...(prev ?? []).filter((row) => row.id !== job.id)])
      setParams({ job: String(job.id) }, { replace: true })
      setFile(null)
      if (fileInput.current) fileInput.current.value = ""
      wake.current()
    } catch (cause) {
      // Отменил сам человек — красной полосы быть не должно.
      if (!(cause instanceof UploadAborted)) {
        setSendError(cause instanceof Error ? cause.message : "Upload failed")
      }
    } finally {
      transfer.current = null
      setSent(null)
    }
  }

  const asked = params.get("job")
  const askedId = asked !== null && /^\d+$/.test(asked) ? Number(asked) : null

  /*
   * Что показано крупно: спрошенная адресом задача, иначе идущая, иначе
   * последняя. Порядок именно такой — вернувшаяся по ссылке видит ту заливку,
   * за которой пришла, а зашедшая с нуля сразу видит ту, что идёт сейчас.
   */
  const shown = React.useMemo(() => {
    if (!jobs || jobs.length === 0) return null
    if (askedId !== null) {
      const match = jobs.find((job) => job.id === askedId)
      if (match) return match
    }
    return jobs.find(isActive) ?? jobs[0]
  }, [jobs, askedId])

  const askedMissing = askedId !== null && jobs !== null && !jobs.some((job) => job.id === askedId)

  return (
    <>
      <PageHeader
        title="Transactions"
        tabs={<TabStrip label="Transactions tabs" tabs={TRANSACTIONS_TABS} />}
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              wake.current()
              setBeat((was) => was + 1)
            }}
            title="Ask the server for the current state now"
          >
            <RotateCw className="h-3.5 w-3.5" strokeWidth={2.5} />
            Refresh
          </Button>
        }
      />

      {/*
       * Наверху и над всем остальным: это ответ на вопрос «хватает ли уже», с
       * которым сюда и приходят. Внутри правой колонки он оказался бы ниже
       * идущей заливки и читался бы как её свойство, а не как свойство всей
       * набранной истории.
       */}
      <Depth beat={beat} />

      <div className="grid gap-4 p-5 lg:grid-cols-[380px_minmax(0,1fr)]">
        <Chooser
          file={file}
          sent={sent}
          error={sendError}
          inputRef={fileInput}
          onPick={(picked) => {
            setFile(picked)
            setSendError(null)
          }}
          onSend={send}
          onCancel={() => transfer.current?.abort()}
        />

        <div className="min-w-0 space-y-4">
          {listError && (
            <Notice tone="error">
              The state could not be fetched — {listError}. The numbers below are from the last
              answer that arrived.
            </Notice>
          )}

          {askedMissing && (
            <Notice tone="warn">
              Upload #{askedId} is not among this account’s uploads. The one below is the most
              recent instead.
            </Notice>
          )}

          {jobs === null ? (
            <section className="space-y-3 rounded border border-line bg-surface p-4 shadow-panel">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-2 w-full" />
              <Skeleton className="h-12 w-full" />
            </section>
          ) : shown ? (
            <JobPanel job={shown} />
          ) : (
            <section className="rounded border border-dashed border-line-strong bg-surface p-10 text-center">
              <p className="text-sm text-fg-muted">Nothing has been uploaded yet.</p>
              <p className="mx-auto mt-1 max-w-sm text-xs text-fg-subtle">
                Pick a SEON export in the upload form. Parsing happens on the server, so the run
                carries on with this tab closed.
              </p>
            </section>
          )}

          {jobs && jobs.length > 1 && (
            <History
              jobs={jobs}
              shownId={shown?.id ?? null}
              onShow={(id) => setParams({ job: String(id) }, { replace: true })}
            />
          )}
        </div>
      </div>
    </>
  )
}

/**
 * Индикатор «сколько истории набралось» (AMEND-16 §3 шаг 4) — строка наверху.
 *
 * Отвечает на вопрос, который иначе приходится задавать вслух: хватает ли
 * залитого правилам. Три вещи в ней обязательны и каждая — от грабель:
 *
 *  1. Период берётся из **реестра покрытия**, а не из `min/max(date)` по
 *     строкам: за строки, о которых реестр не знает, никто не ручается.
 *  2. Непрерывность названа честно. Куски легли не подряд — так и сказано, а
 *     глубина показана по последнему куску. Сумма суток двух кусков выглядела
 *     бы как история, которой нет, и правило, попавшее на дыру, всё равно ушло
 *     бы в пропуск — но уже неожиданно.
 *  3. Нехватка считается **раньше начала**. Окна правил (365 / 1080 / 1825
 *     суток) смотрят назад от скоримой строки, поэтому выгрузка за период после
 *     конца покрытия глубины не прибавляет вовсе, и показывать её как прогресс
 *     нельзя. Про свежий край сказано отдельной фразой — чтобы его тоже долили,
 *     но не путали с приближением к году.
 *
 * Ни одного числа здесь не досчитывается: арифметика на сервере и проверена
 * `pytest` (`tests/test_history_depth.py`). Экран только собирает фразу.
 */
function Depth({ beat }: { beat: number }) {
  const [depth, setDepth] = React.useState<HistoryCoverage | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    let stopped = false
    getCoverage()
      .then((answer) => {
        if (stopped) return
        setDepth(answer)
        setError(null)
      })
      .catch((cause) => {
        // Прошлый ответ остаётся на экране по той же причине, что и у списка:
        // упавший запрос — это «свежее не принесли», а не «истории нет».
        if (!stopped) setError(cause instanceof Error ? cause.message : "Request failed")
      })
    return () => {
      stopped = true
    }
  }, [beat])

  if (!depth) {
    return (
      <div className="border-b border-line bg-surface-sunken px-5 py-2.5">
        {error ? (
          <p className="flex items-start gap-2 text-xs leading-relaxed text-fg-muted">
            <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0 text-review" strokeWidth={2} />
            <span>How much history is in could not be fetched — {error}.</span>
          </p>
        ) : (
          <Skeleton className="h-3 w-96" />
        )}
      </div>
    )
  }

  const gaps = depth.gaps
  const tone = depth.complete
    ? { bg: "bg-approve/5", icon: <Check className="mt-px h-3.5 w-3.5 shrink-0 text-approve" strokeWidth={2.5} /> }
    : gaps.length > 0
      ? { bg: "bg-review/5", icon: <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0 text-review" strokeWidth={2} /> }
      : { bg: "bg-surface-sunken", icon: <Info className="mt-px h-3.5 w-3.5 shrink-0 text-fg-subtle" strokeWidth={2} /> }

  return (
    <div className={cn("border-b border-line px-5 py-2.5", tone.bg)}>
      <p className="flex items-start gap-2 text-xs leading-relaxed text-fg">
        {tone.icon}
        <span>
          <span className="stencil-label mr-1.5 text-fg-subtle">History</span>
          {depth.covered_from === null ? (
            <>
              nothing is in yet. Rules read up to {formatInt(depth.target_days)} days back, so all{" "}
              {formatInt(depth.target_days)} days are missing — and they are missing{" "}
              <em className="not-italic font-semibold">before</em> whatever day the first export
              starts on, not after it.
            </>
          ) : (
            <>
              covered <Day value={depth.covered_from} /> … <Day value={depth.covered_to} />
              {depth.contiguous ? (
                <>
                  , {formatInt(depth.depth_days)} days in a row, no gaps.{" "}
                </>
              ) : (
                <>
                  , but <em className="not-italic font-semibold">not in a row</em>:{" "}
                  {formatInt(gaps.length)} gap{gaps.length > 1 ? "s" : ""}. Unbroken depth is{" "}
                  {formatInt(depth.depth_days)} days, from <Day value={depth.depth_from} /> — a
                  window reaching past that lands in a gap and is skipped with a reason, not scored
                  on less.{" "}
                </>
              )}
              {depth.complete && <>A full year is in.</>}
              {depth.missing_days > 0 && (
                <>
                  {formatInt(depth.missing_days)} days short of the year, all of them{" "}
                  <em className="not-italic font-semibold">earlier than</em>{" "}
                  <Day value={depth.depth_from} />.{" "}
                </>
              )}
              {depth.stale_days > 0 && (
                <>
                  The {formatInt(depth.stale_days)} days after <Day value={depth.covered_to} /> are
                  not in either — filling them keeps the edge current, it does not shorten the
                  shortfall above.
                </>
              )}
            </>
          )}
        </span>
      </p>

      {/*
       * Дыры — датами, а не числом: «одна дыра» не говорит, какую выгрузку
       * заказывать, а «2/1/2026 … 9/14/2026» говорит.
       */}
      {gaps.length > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1 pl-[22px]">
          <span className="text-xs text-fg-muted">Missing inside:</span>
          {gaps.map((gap) => (
            <Hole key={`${gap.day_from}-${gap.day_to}`} gap={gap} />
          ))}
        </div>
      )}

      {error && (
        <p className="mt-1 pl-[22px] text-xs text-fg-subtle">
          Last answer from the server; the latest request failed — {error}.
        </p>
      )}
    </div>
  )
}

function Hole({ gap }: { gap: Stretch }) {
  return (
    <span className="rounded-sm border border-line-strong bg-surface px-1.5 py-0.5 text-xs text-fg">
      <Day value={gap.day_from} /> … <Day value={gap.day_to} />
      <span className="text-fg-subtle"> ({formatInt(gap.days)} days)</span>
    </span>
  )
}

/** Левая панель: выбор файла, отправка и то, что о ней надо знать заранее. */
function Chooser({
  file,
  sent,
  error,
  inputRef,
  onPick,
  onSend,
  onCancel,
}: {
  file: File | null
  sent: number | null
  error: string | null
  inputRef: React.RefObject<HTMLInputElement>
  onPick: (file: File | null) => void
  onSend: () => void
  onCancel: () => void
}) {
  const sending = sent !== null
  const size = file?.size ?? 0
  const percent = sending && size > 0 ? Math.min(100, (sent / size) * 100) : 0

  return (
    <section className="animate-panel-in h-fit rounded border border-line bg-surface shadow-panel">
      <header className="border-b border-line px-4 py-3">
        <h2 className="card-title">Upload an export</h2>
        <p className="mt-1 text-xs leading-relaxed text-fg-subtle">
          A SEON transactions export, CSV, semicolon-separated — the file as the service hands it
          out, without editing.
        </p>
      </header>

      <div className="space-y-3 px-4 py-3">
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          disabled={sending}
          aria-label="Export file"
          onChange={(event) => onPick(event.target.files?.[0] ?? null)}
          className="block w-full cursor-pointer text-xs text-fg-muted file:mr-2.5 file:cursor-pointer file:rounded file:border file:border-line-strong file:bg-surface-sunken file:px-2.5 file:py-1.5 file:text-[13px] file:font-medium file:text-fg hover:file:bg-surface disabled:cursor-not-allowed"
        />

        {file && (
          <p className="truncate text-xs text-fg" title={file.name}>
            {file.name} · {formatBytes(file.size)}
          </p>
        )}

        {sending ? (
          <div className="space-y-2">
            <Bar percent={percent} label="Sending to the server" />
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-fg-muted">
                {formatBytes(sent)} of {formatBytes(size)}
              </span>
              <Button variant="ghost" size="sm" onClick={onCancel}>
                <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                Cancel
              </Button>
            </div>
            <p className="text-xs leading-relaxed text-review">
              Keep this tab open until the bar fills — a closed tab stops the transfer. What
              survives a closed tab is the parsing, which starts once the file is here.
            </p>
          </div>
        ) : (
          <Button variant="primary" className="w-full" disabled={!file} onClick={onSend}>
            <FileUp className="h-3.5 w-3.5" strokeWidth={2.5} />
            Upload
          </Button>
        )}

        {error && <Notice tone="error">{error}</Notice>}

        <div className="space-y-1.5 border-t border-line pt-3 text-xs leading-relaxed text-fg-subtle">
          <p className="flex items-start gap-2">
            <Info className="mt-px h-3.5 w-3.5 shrink-0 text-fg-subtle" strokeWidth={2} />
            <span>
              Parsing runs on the server and keeps going with the tab closed. Come back to this page
              and the progress is here — it is read from the server, not remembered by the browser.
            </span>
          </p>
          <p className="flex items-start gap-2">
            <Info className="mt-px h-3.5 w-3.5 shrink-0 text-fg-subtle" strokeWidth={2} />
            <span>
              A row already in the table is skipped by transaction id, so uploading the same export
              twice does not change the row count.
            </span>
          </p>
          <p className="flex items-start gap-2">
            <Info className="mt-px h-3.5 w-3.5 shrink-0 text-fg-subtle" strokeWidth={2} />
            <span>
              Nothing out of the file is shown on this screen: only counts, column names and the
              period the file covers.
            </span>
          </p>
        </div>
      </div>
    </section>
  )
}

/** Полоса хода. Своя, а не `<progress>`: тому не задать цвета консоли. */
function Bar({ percent, label }: { percent: number; label: string }) {
  const value = Math.max(0, Math.min(100, percent))
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value)}
      className="h-2 w-full overflow-hidden rounded-sm border border-line-strong bg-surface-sunken"
    >
      <div
        className="h-full bg-accent transition-[width] duration-300 ease-snap"
        style={{ width: `${value}%` }}
      />
    </div>
  )
}

function JobPanel({ job }: { job: Upload }) {
  const running = isActive(job)

  return (
    <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
      <header className="flex flex-wrap items-start justify-between gap-2 border-b border-line px-4 py-3">
        <div className="min-w-0">
          <h2 className="card-title">Upload #{job.id}</h2>
          <p className="mt-0.5 truncate text-xs text-fg-muted" title={job.source_file}>
            {job.source_file} · {formatBytes(job.size_bytes)}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/*
           * Число попыток — не техническая мелочь: это единственное место, где
           * видно, что заливку подхватили после обрыва. Без него докачка
           * выглядела бы как новая задача, начавшаяся сама по себе.
           */}
          {job.attempts > 1 && <Badge tone="review">resumed ×{job.attempts - 1}</Badge>}
          <Badge tone={STATUS_TONE[job.status]}>{job.status}</Badge>
        </div>
      </header>

      <div className="space-y-3 px-4 py-3">
        <div className="space-y-1.5">
          <Bar percent={job.progress.percent} label={`Upload ${job.id} parsed`} />
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <span className="text-xs text-fg">
              {job.progress.percent.toFixed(1)}% of the file parsed
            </span>
            <span className="text-xs text-fg-subtle">
              {formatBytes(job.progress.bytes_done)} of {formatBytes(job.progress.bytes_total)}
            </span>
          </div>
        </div>

        <Counts report={job.report} running={running} />
      </div>

      {job.attempts > 1 && (
        <p className="flex items-start gap-2 border-t border-line bg-review/5 px-4 py-2.5 text-xs leading-relaxed text-review">
          <Info className="mt-px h-3.5 w-3.5 shrink-0" strokeWidth={2} />
          <span>
            This run was interrupted and picked up again — {formatInt(job.attempts)} attempts on the
            same job #{job.id}. It continued from the byte it had reached, not from the start: the
            counts above carry over from before the break.
          </span>
        </p>
      )}

      {job.status === "failed" && (
        <p className="flex items-start gap-2 border-t border-line bg-decline/5 px-4 py-2.5 text-xs leading-relaxed text-decline">
          <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0" strokeWidth={2} />
          <span>
            The run stopped on <span>{job.failure ?? EMPTY}</span>.{" "}
            {job.report.read > 0 ? (
              <>
                What had been parsed by then is already in the table and is not lost; uploading the
                same file again carries on from there rather than doubling the rows — rows already
                present are skipped by transaction id.
              </>
            ) : (
              <>
                Not a single row was read, so nothing of this file reached the table. The file
                itself was turned down, not the rows in it: check that it is the transactions
                export and that its first line is the header row with{" "}
                <span className="font-mono">id</span> in it. Sending the very same file again
                would stop in the very same place.
              </>
            )}
          </span>
        </p>
      )}

      {job.status === "done" && <Coverage job={job} />}

      <Columns report={job.report} />

      <footer className="flex flex-wrap gap-x-6 gap-y-1 border-t border-line px-4 py-2 text-xs text-fg-subtle">
        <span>
          Started {job.started_at ? stamp(job.started_at) : "not yet"}
        </span>
        {job.finished_at && <span>Finished {stamp(job.finished_at)}</span>}
      </footer>
    </section>
  )
}

/**
 * Четыре числа разбора.
 *
 * `read` и `inserted` стоят рядом намеренно: полоса выше говорит про **файл**,
 * а эти — про **таблицу**, и путать их нельзя. Файл, целиком состоящий из
 * дублей, разбирается на 100 % и не прибавляет ни строки; подпись под
 * счётчиками говорит об этом словами, а не оставляет читателя гадать, почему
 * «залито 100 %», а строк не прибавилось.
 */
function Counts({ report, running }: { report: UploadReport; running: boolean }) {
  const cells: { label: string; value: number; tone?: string }[] = [
    { label: "Read", value: report.read },
    { label: "Inserted", value: report.inserted, tone: "text-approve" },
    { label: "Duplicates", value: report.duplicates },
    { label: "Bad rows", value: report.bad_rows, tone: report.bad_rows > 0 ? "text-review" : "" },
  ]

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {cells.map((cell) => (
          <div key={cell.label} className="rounded-sm border border-line bg-surface-sunken px-2.5 py-2">
            <span className="stencil-label block text-fg-subtle">{cell.label}</span>
            <span className={cn("text-lg font-medium text-fg", cell.tone)}>
              {formatInt(cell.value)}
            </span>
          </div>
        ))}
      </div>

      {report.read > 0 && report.inserted < report.read && (
        <p className="text-xs leading-relaxed text-fg-muted">
          {formatInt(report.read)} rows were read and {formatInt(report.inserted)} landed in the
          table: {formatInt(report.duplicates)} were already there and{" "}
          {formatInt(report.bad_rows)} could not be read. The bar counts the file; these count the
          table.
        </p>
      )}

      {running && (
        <p className="text-xs leading-relaxed text-fg-subtle">
          These are not an estimate — they are written together with the rows they count, so they
          never run ahead of what the table has actually taken.
        </p>
      )}
    </div>
  )
}

/** Период, который файл реально принёс, — из реестра покрытия. */
function Coverage({ job }: { job: Upload }) {
  const { covers_from: from, covers_to: to, rows_covered: rows } = job.report

  if (!from || !to) {
    return (
      <p className="flex items-start gap-2 border-t border-line px-4 py-2.5 text-xs leading-relaxed text-fg-muted">
        <Info className="mt-px h-3.5 w-3.5 shrink-0 text-fg-subtle" strokeWidth={2} />
        <span>
          The run finished without adding a period to the coverage registry — no row carried a date
          it could claim.
        </span>
      </p>
    )
  }

  return (
    <p className="flex items-start gap-2 border-t border-line bg-approve/5 px-4 py-2.5 text-xs leading-relaxed text-fg">
      <Check className="mt-px h-3.5 w-3.5 shrink-0 text-approve" strokeWidth={2.5} />
      <span>
        Covers <span>{formatDateFull(from)}</span> …{" "}
        <span>{formatDateFull(to)}</span> — {formatInt(rows)} rows, registry
        entry <span>#{job.coverage_id ?? EMPTY}</span>. Velocity windows read
        this registry, so a rule asking for a period is answered by what the file actually brought,
        not by the earliest date in the table.
      </span>
    </p>
  )
}

/**
 * Колонки, о которых схема не знает, и те, которых файлу не хватило.
 *
 * Имена — единственное, что отсюда выходит наружу: ни одного значения. Пустой
 * список не рисуется вовсе — рамка, появляющаяся каждую заливку, перестаёт
 * значить что-либо к третьей.
 */
function Columns({ report }: { report: UploadReport }) {
  if (report.unknown_columns.length === 0 && report.missing_columns.length === 0) return null

  return (
    <div className="space-y-2 border-t border-line px-4 py-2.5">
      {report.unknown_columns.length > 0 && (
        <ColumnList
          title={`${formatInt(report.unknown_columns.length)} columns in the file are not in the schema`}
          note="They were read past and stored nowhere. Harmless in itself — unless it is a column the rules were supposed to read, under a name the schema does not know."
          names={report.unknown_columns}
        />
      )}
      {report.missing_columns.length > 0 && (
        <ColumnList
          title={`${formatInt(report.missing_columns.length)} columns the schema expects were not in the file`}
          note="Rules that read them have nothing to read for these rows."
          names={report.missing_columns}
        />
      )}
    </div>
  )
}

function ColumnList({ title, note, names }: { title: string; note: string; names: string[] }) {
  return (
    <div>
      <p className="flex items-start gap-2 text-xs font-medium leading-relaxed text-review">
        <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0" strokeWidth={2} />
        <span>{title}</span>
      </p>
      <div className="mt-1.5 flex flex-wrap gap-1">
        {names.map((name) => (
          <span
            key={name}
            className="rounded-sm border border-line-strong bg-surface-sunken px-1.5 py-0.5 font-mono text-xs text-fg"
          >
            {name}
          </span>
        ))}
      </div>
      <p className="mt-1.5 text-xs leading-relaxed text-fg-subtle">{note}</p>
    </div>
  )
}

/** Прошлые заливки. Номер — кнопка: он же и адрес, по которому экран вернётся. */
function History({
  jobs,
  shownId,
  onShow,
}: {
  jobs: Upload[]
  shownId: number | null
  onShow: (id: number) => void
}) {
  return (
    <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
      <header className="border-b border-line px-4 py-3">
        <h3 className="section-title">Earlier uploads</h3>
      </header>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] table-fixed border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left">
              <th className="stencil-label w-[9%] px-4 py-2 font-medium text-fg-subtle">#</th>
              <th className="stencil-label w-[30%] px-3 py-2 font-medium text-fg-subtle">File</th>
              <th className="stencil-label w-[14%] px-3 py-2 font-medium text-fg-subtle">Status</th>
              <th className="stencil-label w-[11%] px-3 py-2 text-right font-medium text-fg-subtle">
                Read
              </th>
              <th className="stencil-label w-[11%] px-3 py-2 text-right font-medium text-fg-subtle">
                Inserted
              </th>
              <th className="stencil-label w-[11%] px-3 py-2 text-right font-medium text-fg-subtle">
                Dupes
              </th>
              <th className="stencil-label px-4 py-2 font-medium text-fg-subtle">Started</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((job, index) => (
              <tr
                key={job.id}
                aria-current={job.id === shownId ? "true" : undefined}
                className={cn(
                  "animate-row-in border-b border-line/60 last:border-b-0",
                  job.id === shownId && "bg-accent-soft",
                )}
                style={{ animationDelay: `${Math.min(index, 20) * 8}ms` }}
              >
                <td className="px-4 py-2 align-top">
                  <button
                    type="button"
                    onClick={() => onShow(job.id)}
                    className="text-xs text-accent hover:underline"
                  >
                    #{job.id}
                  </button>
                </td>
                <td className="truncate px-3 py-2 align-top text-fg" title={job.source_file}>
                  {job.source_file}
                </td>
                <td className="px-3 py-2 align-top">
                  <Badge tone={STATUS_TONE[job.status]}>{job.status}</Badge>
                </td>
                <td className="px-3 py-2 text-right align-top text-fg-muted">
                  {formatInt(job.report.read)}
                </td>
                <td className="px-3 py-2 text-right align-top text-fg">
                  {formatInt(job.report.inserted)}
                </td>
                <td className="px-3 py-2 text-right align-top text-fg-subtle">
                  {formatInt(job.report.duplicates)}
                </td>
                <td className="px-4 py-2 align-top text-xs text-fg-muted">
                  {job.started_at ? stamp(job.started_at) : EMPTY}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

/** `9/18/2026 2:44:02 PM` — как в остальной консоли. */
function stamp(value: string) {
  return `${formatDateFull(value)} ${formatTime(value)}`.trim()
}
